import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";

const firebaseConfig = {
  apiKey: "AIzaSyDiYYll0xUEoBj6jDYgqyDFh2mwhu6VHdg",
  authDomain: "tu-ruta-segura-app.firebaseapp.com",
  projectId: "tu-ruta-segura-app",
  storageBucket: "tu-ruta-segura-app.firebasestorage.app",
  messagingSenderId: "777879218753",
  appId: "1:777879218753:web:0eb0de8d66fb4a139a74c0",
  measurementId: "G-NYKJM89494",
};

// VAPID Key pública (debe coincidir con Firebase Console → Cloud Messaging → Web Push certificates)
const VAPID_KEY = "BDcCLl_FAh7FhFgptXpjoYewuhRiGrh8ulp9hLKbPiNoXbGeh6QGUSUoPzIt3ClyZ8HnYSaDJ4i4S06ebNydCXU";

// Expiración del token: 30 días en ms
const TOKEN_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const TOKEN_TIMESTAMP_KEY = "fcm_token_registered_at";
const TOKEN_VAPID_KEY = "fcm_token_vapid";

// Inicializar Firebase
const firebaseApp = initializeApp(firebaseConfig);
const messaging = getMessaging(firebaseApp);

/**
 * Determina si se necesita forzar una re-suscripción push.
 * Retorna true si:
 * - El token tiene más de 30 días
 * - La VAPID key cambió desde la última vez
 * - No hay registro previo de la fecha de registro
 */
const shouldForceRefresh = () => {
  const registeredAt = localStorage.getItem(TOKEN_TIMESTAMP_KEY);
  const storedVapid = localStorage.getItem(TOKEN_VAPID_KEY);

  // Si la VAPID key cambió, siempre refrescar
  if (storedVapid !== VAPID_KEY) {
    console.log("[FCM] VAPID key distinta detectada → forzando re-suscripción");
    return true;
  }

  // Si no hay timestamp, refrescar
  if (!registeredAt) return true;

  // Si el token tiene más de 30 días, refrescar
  const age = Date.now() - parseInt(registeredAt, 10);
  if (age > TOKEN_TTL_MS) {
    console.log("[FCM] Token mayor a 30 días → forzando re-suscripción");
    return true;
  }

  return false;
};

/**
 * Elimina la suscripción push existente del browser.
 * Se llama cuando se detecta que el token necesita renovarse.
 */
const clearExistingPushSubscription = async (registration) => {
  try {
    const existingSubscription = await registration.pushManager.getSubscription();
    if (existingSubscription) {
      await existingSubscription.unsubscribe();
      console.log("[FCM] Suscripción push anterior eliminada correctamente");
    }
  } catch (err) {
    // No crítico: si falla, getToken() intentará crear una nueva igual
    console.warn("[FCM] No se pudo eliminar la suscripción anterior:", err.message);
  }
};

/**
 * Guarda en localStorage la fecha y VAPID key del token actual.
 * Usado para detectar tokens viejos o con VAPID key incorrecta.
 */
const saveTokenMetadata = () => {
  localStorage.setItem(TOKEN_TIMESTAMP_KEY, Date.now().toString());
  localStorage.setItem(TOKEN_VAPID_KEY, VAPID_KEY);
};

/**
 * Solicita o renueva el token FCM del dispositivo.
 *
 * Lógica de refresco automático:
 * - Si la VAPID key cambió → elimina la suscripción vieja y crea una nueva
 * - Si el token tiene más de 30 días → renueva automáticamente
 * - Si es el primer registro → crea la suscripción normalmente
 *
 * El usuario final nunca necesita hacer nada manualmente.
 */
export const requestForToken = async () => {
  try {
    // Verificar si el navegador soporta service workers
    if (!("serviceWorker" in navigator)) {
      console.warn("[FCM] Los Service Workers no están soportados en este navegador");
      return null;
    }

    // Registrar el service worker
    const registration = await navigator.serviceWorker.register(
      "/firebase-messaging-sw.js",
      { scope: "/" }
    );

    // Esperar a que el service worker esté activo
    await navigator.serviceWorker.ready;

    // Si se detecta que el token necesita renovarse, limpiar la suscripción antigua
    if (shouldForceRefresh()) {
      await clearExistingPushSubscription(registration);
    }

    // Obtener el token FCM (fresco o cacheado según el estado anterior)
    const currentToken = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (currentToken) {
      // Guardar metadata para detectar futuras necesidades de refresco
      saveTokenMetadata();
      console.log("[FCM] Token FCM obtenido con éxito");
      return currentToken;
    } else {
      console.warn("[FCM] No se pudo obtener el token. Solicite permiso para notificaciones.");
      return null;
    }
  } catch (err) {
    console.error("[FCM] Error al recuperar el token FCM:", err);

    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isMacOS = /mac os/.test(userAgent) && !window.MSStream;

    if (isIOS) {
      console.info("[FCM] Safari en iOS tiene soporte limitado para notificaciones web push");
    } else if (isMacOS) {
      console.info("[FCM] Safari en macOS requiere configuración adicional para notificaciones web push");
    }

    return null;
  }
};

// Escuchar notificaciones en primer plano
export const setupForegroundNotification = () => {
  // Función para detectar el sistema operativo
  const isAppleDevice = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod|mac/.test(userAgent) && !window.MSStream;
  };

  onMessage(messaging, (payload) => {
    console.log("[FCM] Mensaje recibido en primer plano:", payload);

    // Extraer la URL de redirección de los datos del payload (si existe)
    let redirectUrl = payload.data?.url || "/";

    // Verificar si la URL es absoluta o relativa
    const isAbsoluteUrl = redirectUrl.startsWith("http");

    console.log(`[FCM] URL de redirección: ${redirectUrl} (${isAbsoluteUrl ? "absoluta" : "relativa"})`);

    // Notificación estándar para la mayoría de los navegadores
    if (!isAppleDevice() && "Notification" in window) {
      const notification = new Notification(payload.notification.title, {
        body: payload.notification.body,
        icon: payload.notification.icon || "/fav/favicon-32x32.png",
        data: { url: redirectUrl },
      });

      // Manejar el clic en la notificación
      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        window.location.href = redirectUrl;
        notification.close();
      };
    }
    // Para dispositivos Apple, crear una notificación visual en la interfaz
    else if (isAppleDevice()) {
      const notificationEl = document.createElement("div");
      notificationEl.className = "apple-notification";
      notificationEl.innerHTML = `
        <div class="apple-notification-content">
          <h4>${payload.notification.title}</h4>
          <p>${payload.notification.body}</p>
        </div>
      `;

      Object.assign(notificationEl.style, {
        position: "fixed",
        top: "20px",
        right: "20px",
        zIndex: "9999",
        padding: "15px",
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        color: "white",
        borderRadius: "10px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
        maxWidth: "300px",
        cursor: "pointer",
        animation: "fadeIn 0.5s, fadeOut 0.5s 4.5s",
      });

      notificationEl.addEventListener("click", () => {
        window.location.href = redirectUrl;
      });

      document.body.appendChild(notificationEl);

      setTimeout(() => {
        if (notificationEl && notificationEl.parentNode) {
          notificationEl.parentNode.removeChild(notificationEl);
        }
      }, 5000);

      if (!document.getElementById("apple-notification-styles")) {
        const styleEl = document.createElement("style");
        styleEl.id = "apple-notification-styles";
        styleEl.textContent = `
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes fadeOut { from { opacity: 1; } to { opacity: 0; } }
        `;
        document.head.appendChild(styleEl);
      }
    }
  });
};
